import { useState, useEffect } from "react";
import api from "../../lib/apiClient";
import { cacheGet, cacheSet } from "../../lib/cache";

// Defensive helper — coerce any API payload that's supposed to be a list
// into an actual JS array. The web build NEVER hits this (CORS works fine,
// backend always returns proper JSON arrays), but the iOS / Android binary
// running inside Capacitor's WKWebView can receive an HTML error page or
// `{detail: "..."}` JSON whenever the backend is briefly slow or a network
// hiccup interrupts the response — and `.map` / `.forEach` on a non-array
// crashes the entire React tree before the user sees anything beyond the
// splash. Wrapping every `set...(res.data)` call in this helper keeps the
// app rendering an empty state gracefully instead.
const asArray = (data) => (Array.isArray(data) ? data : []);

/**
 * useFeedSource — bundles the four data fetches that drive the public
 * feed/landing:
 *   1. main feed (`/videos` or `/feed/following`, SWR-cached)
 *   2. featured videos (`/videos/featured`, one-shot)
 *   3. trending videos (`/videos/trending?hours=6`, 5-min poll, paused
 *      when tab is hidden)
 *   4. source-coercion: when the user logs out while on the Following
 *      view, snap back to Global (otherwise the feed sticks empty).
 *
 * Also re-fetches the global feed when the Header logo dispatches
 * `globaleyes:reset-feed-source` — used as an escape hatch for users
 * stuck on an empty Following view.
 *
 * The `setVideos` / `setFeaturedVideos` setters are exposed so the parent
 * can splice in optimistic updates after a follow-toggle, applaud, or
 * commission completion (handlers in FeedPage refresh `videos` after
 * those mutations). The polling handlers (trending) are not exposed
 * because nothing else in the tree needs to write trending data.
 *
 * Inputs:
 *   initialSource — 'all' | 'following', the route-mounted default
 *                   (Home = 'all', /feed = 'following').
 *   token         — JWT; null when logged out. Used both as a fetch
 *                   trigger and as the gate for Following mode.
 *
 * Returns: { source, setSource, videos, setVideos, featuredVideos,
 *            setFeaturedVideos, trendingVideos, loading, setLoading }
 */
export function useFeedSource({ initialSource, token, user }) {
  // `source` lives only for the lifetime of an open player session.
  // Every time the user returns to the Home dynamic screen — whether by
  // mounting the route fresh or by closing & reopening the player — the
  // toggle defaults back to the route's initialSource. Per spec: no
  // persistence across mounts.
  const [source, setSource] = useState(initialSource);
  const [videos, setVideos] = useState([]);
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [trendingVideos, setTrendingVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  // Coerce stale "following" preference back to "all" if the user logged
  // out — otherwise the feed sticks on an endpoint that always returns
  // empty for anonymous viewers.
  useEffect(() => {
    if (!token && source === "following") {
      setSource("all");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Header's Home link & GlobalEyes.TV logo dispatch this when clicked
  // so a user who's stuck on an empty Following view can always escape
  // back to the Global playlist — even though they're already on `/` and
  // React Router wouldn't otherwise re-render anything.
  useEffect(() => {
    const handler = () => setSource("all");
    window.addEventListener("globaleyes:reset-feed-source", handler);
    return () => window.removeEventListener("globaleyes:reset-feed-source", handler);
  }, []);

  // 1: Main feed — SWR-cached.
  useEffect(() => {
    const cacheKey = source === "following" ? "followingFeed" : "videos";
    const fetchVideos = async () => {
      const cached = cacheGet(cacheKey);
      if (cached) {
        setVideos(asArray(cached));
        setLoading(false);
      } else {
        setLoading(true);
      }
      try {
        if (source === "following") {
          if (!token) {
            setVideos([]);
            cacheSet(cacheKey, []);
            setLoading(false);
            return;
          }
          const res = await api.get(`/feed/following`);
          const list = asArray(res.data);
          setVideos(list);
          cacheSet(cacheKey, list);
        } else {
          // Referral feed-boost: a newly-referred user's very first
          // global-feed mount in this tab gets up to 3 of their
          // referrer's most recent videos prepended. Turns an empty-
          // feeling day-1 home page into "I'm already watching the
          // person who invited me." We bypass the SWR cache for this
          // single boosted fetch — otherwise a previously-cached non-
          // boosted list would short-circuit the boost. The session-
          // storage gate makes it strictly first-load only.
          let boostHandle = null;
          try {
            const consumed = sessionStorage.getItem("feed_boost_consumed_v1");
            if (!consumed && user?.referred_by_username) {
              boostHandle = user.referred_by_username;
            }
          } catch { /* private mode — skip the boost */ }
          const qs = boostHandle ? `?boost_user=${encodeURIComponent(boostHandle)}` : "";
          const res = await api.get(`/videos${qs}`);
          const list = asArray(res.data);
          setVideos(list);
          // Only cache the NON-boosted shape; otherwise SWR would
          // serve the boosted list to every future tab/session.
          if (!boostHandle) cacheSet(cacheKey, list);
          if (boostHandle) {
            try { sessionStorage.setItem("feed_boost_consumed_v1", "1"); }
            catch { /* swallow */ }
          }
        }
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    fetchVideos();
  }, [source, token, user?.referred_by_username]);

  // 2: Featured (one-shot, SWR-cached across Home + Profile mounts).
  useEffect(() => {
    const fetchFeatured = async () => {
      const cached = cacheGet("featured");
      if (cached) setFeaturedVideos(asArray(cached));
      try {
        const res = await api.get(`/videos/featured`);
        const list = asArray(res.data);
        setFeaturedVideos(list);
        cacheSet("featured", list);
      } catch (err) {
        console.error("Failed to fetch featured videos:", err);
      }
    };
    fetchFeatured();
  }, []);

  // 3: Trending — last 6 hours by loop velocity. Refreshed every 5 min
  //    (paused while tab is hidden, like the other pollers on this page).
  //    SWR-cached so navigating between Home and Profile doesn't trigger
  //    a redundant refetch when the cache is still fresh.
  useEffect(() => {
    let cancelled = false;
    const fetchTrending = async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const res = await api.get(`/videos/trending?hours=6&limit=6`);
        if (!cancelled) {
          const list = asArray(res.data);
          setTrendingVideos(list);
          cacheSet("trending", list);
        }
      } catch (err) {
        // Trending is best-effort; the feed still works without it.
        // Log for visibility so a repeated failure shows up in dev tools.
        console.warn("Failed to fetch trending videos:", err);
      }
    };
    const cached = cacheGet("trending");
    if (cached) setTrendingVideos(asArray(cached));
    fetchTrending();
    const id = setInterval(fetchTrending, 5 * 60 * 1000);
    const onVis = () => { if (!document.hidden) fetchTrending(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return {
    source, setSource,
    videos, setVideos,
    featuredVideos, setFeaturedVideos,
    trendingVideos,
    loading, setLoading,
  };
}
